import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, MapPin, X, ArrowUpRight, PartyPopper, Bell, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Events() {
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("upcoming");
  const [selected, setSelected] = useState(null);
  const [reminderEmail, setReminderEmail] = useState("");
  const [reminderBusy, setReminderBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setReminderEmail("");
    setReminderBusy(false);
  }, [selected]);

  const subscribeReminder = async (e) => {
    e.preventDefault();
    if (reminderBusy || !selected) return;
    setReminderBusy(true);
    try {
      const r = await axios.post(`${API}/reminders`, {
        email: reminderEmail,
        event_id: selected.id,
        event_title: selected.title,
      });
      toast.success(r.data.already ? "You're already on the list for this one." : "On the list — we'll nudge you before race day.");
      setReminderEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your reminder");
    } finally {
      setReminderBusy(false);
    }
  };

  const visible = events.filter((e) => e.status === tab);

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <PageHeader />
      <main className="px-6 sm:px-12 lg:px-20 pt-32 sm:pt-40 pb-24 sm:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <PartyPopper className="w-4 h-4" /> Gatherings & Race Days
          </p>
          <h1 data-testid="events-headline" className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#0D1117] tracking-tight leading-[1.06]">
            Fitygrity <span className="italic text-[#FF4E00]">Events</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#374151] leading-relaxed">
            Community runs, finisher evenings and race-weekend rituals — the social heartbeat of the tribe.
            For the full world calendar, see the <Link to="/calendar" className="text-[#FF4E00] underline underline-offset-4">Race Calendar</Link>.
          </p>
        </motion.div>

        <div className="flex gap-2 mb-12" data-testid="events-tabs">
          {["upcoming", "past"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`events-tab-${t}`}
              className={`font-mono text-[11px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-full border transition-all duration-300 ${
                tab === t
                  ? "bg-[#FF4E00] border-[#FF4E00] text-[#FBF9F5]"
                  : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00]/50 hover:text-[#FF4E00]"
              }`}
            >
              {t === "upcoming" ? "Upcoming Events" : "Past Events"}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((e, i) => (
            <motion.button
              key={e.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: "easeOut" }}
              onClick={() => setSelected(e)}
              data-testid="event-card"
              className="group text-left rounded-3xl border border-[#0D1117]/10 hover:border-[#FF4E00]/40 bg-[#FFFFFF] p-7 transition-all duration-300 hover:-translate-y-1"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4E00] flex items-center gap-2">
                <CalendarDays className="w-3.5 h-3.5" /> {e.date}
              </p>
              <h2 className="font-serif text-xl sm:text-2xl text-[#0D1117] mt-4 group-hover:text-[#FF4E00] transition-colors duration-300">
                {e.title}
              </h2>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mt-2 flex items-center gap-1.5">
                <MapPin className="w-3 h-3" /> {e.city}
              </p>
              <p className="text-sm text-[#374151] leading-relaxed mt-4 line-clamp-3">{e.description}</p>
              <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4E00]">
                Details <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </motion.button>
          ))}
        </div>
        {visible.length === 0 && (
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#6B7280]">Nothing here yet — check the other tab.</p>
        )}
      </main>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#0D1117]/60 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setSelected(null)}
            data-testid="event-detail-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#FBF9F5] rounded-3xl max-w-lg w-full p-8 sm:p-10 relative"
            >
              <button onClick={() => setSelected(null)} className="absolute top-5 right-5 text-[#6B7280] hover:text-[#FF4E00]" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">{selected.status} · {selected.date}</p>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#0D1117] mt-4">{selected.title}</h2>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mt-3 flex items-center gap-1.5">
                <MapPin className="w-3 h-3" /> {selected.city}
              </p>
              <p className="text-sm sm:text-base text-[#374151] leading-relaxed mt-5">{selected.description}</p>
              {selected.reg_url && selected.status === "upcoming" && (
                selected.reg_url.startsWith("/") ? (
                  <Link
                    to={selected.reg_url}
                    data-testid="event-register-link"
                    className="mt-7 inline-flex items-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-7 py-3.5 rounded-full transition-colors duration-300"
                  >
                    Register Interest <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                ) : (
                  <a
                    href={selected.reg_url}
                    target="_blank"
                    rel="noopener"
                    data-testid="event-register-link"
                    className="mt-7 inline-flex items-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-7 py-3.5 rounded-full transition-colors duration-300"
                  >
                    Register <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                )
              )}
              {selected.status === "upcoming" && (
                <form onSubmit={subscribeReminder} className="mt-7 pt-6 border-t border-[#0D1117]/10" data-testid="event-reminder-form">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151] flex items-center gap-2 mb-3">
                    <Bell className="w-3.5 h-3.5 text-[#FF4E00]" /> Get a nudge before race day
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      required
                      placeholder="you@email.com"
                      value={reminderEmail}
                      onChange={(e) => setReminderEmail(e.target.value)}
                      data-testid="event-reminder-email"
                      className="flex-1 min-w-0 bg-[#FFFFFF] border border-[#0D1117]/15 rounded-full px-5 py-3 text-sm text-[#0D1117] placeholder:text-[#6B7280] focus:border-[#FF4E00]/60 focus:outline-none transition-all duration-300"
                    />
                    <button
                      type="submit"
                      disabled={reminderBusy}
                      data-testid="event-reminder-submit"
                      className="shrink-0 inline-flex items-center gap-2 border border-[#FF4E00]/50 hover:bg-[#FF4E00] hover:text-[#FBF9F5] text-[#FF4E00] font-mono text-[11px] uppercase tracking-[0.15em] px-5 py-3 rounded-full transition-all duration-300"
                    >
                      {reminderBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Remind Me"}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
